/**
 * Tickets & Issues Management API Service
 *
 * Handles all ticket/maintenance issue API calls:
 * - Get/create/update tickets
 * - Schedule tickets
 * - Ticket comments and attachments
 * - Problem types management
 * - Service providers management
 * - Ticket analytics
 */

import { post, uploadFile } from './client';

export const ticketsApi = {
    /**
     * Get all tickets for a landlord
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of tickets
     */
    getAll: async (landlordId) => {
        return post('pegasus/visionary/tickets/api/tickets', {
            landlord_id: landlordId,
        });
    },

    /**
     * Get specific ticket details
     * @param {string} ticketId - Ticket ID
     * @returns {Promise<Object>} Ticket details
     */
    getById: async (ticketId) => {
        return post('pegasus/visionary/tickets/api/tickets/get', {
            ticket_id: ticketId,
        });
    },

    /**
     * Update a ticket
     * @param {Object} updateData
     * @param {string} updateData.ticket_id - Ticket ID
     * @returns {Promise<Object>} Update response
     */
    update: async (updateData) => {
        return post('pegasus/visionary/tickets/api/tickets/update', updateData);
    },

    /**
     * Bulk update multiple tickets
     * @param {Object} bulkData
     * @param {Array} bulkData.ticket_ids - Array of ticket IDs
     * @returns {Promise<Object>} Bulk update response
     */
    bulkUpdate: async (bulkData) => {
        return post('pegasus/visionary/tickets/api/tickets/bulk/update', bulkData);
    },

    /**
     * Schedule a ticket
     * @param {Object} scheduleData
     * @param {string} scheduleData.ticket_id - Ticket ID
     * @returns {Promise<Object>} Schedule response
     */
    schedule: async (scheduleData) => {
        return post('pegasus/visionary/tickets/api/tickets/schedule', scheduleData);
    },

    /**
     * Bulk schedule multiple tickets
     * @param {Object} scheduleData
     * @param {Array} scheduleData.ticket_ids - Array of ticket IDs
     * @returns {Promise<Object>} Bulk schedule response
     */
    bulkSchedule: async (scheduleData) => {
        return post('pegasus/visionary/tickets/api/tickets/bulk/schedule', scheduleData);
    },

    /**
     * Add comment to a ticket
     * @param {Object} commentData
     * @param {string} commentData.ticket_id - Ticket ID
     * @param {string} commentData.comment - Comment text
     * @param {string} commentData.commenter_id - Commenter ID
     * @returns {Promise<Object>} Add comment response
     */
    addComment: async (commentData) => {
        return post('pegasus/visionary/tickets/api/tickets/comment/add', commentData);
    },

    /**
     * Upload attachment to a ticket
     * @param {string} ticketId - Ticket ID
     * @param {File} file - File to upload
     * @returns {Promise<Object>} Upload response
     */
    uploadAttachment: async (ticketId, file) => {
        const formData = new FormData();
        formData.append('ticket_id', ticketId);
        formData.append('file', file);

        return uploadFile('pegasus/visionary/tickets/api/tickets/attachment/upload', formData);
    },

    /**
     * Get ticket analytics
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} Analytics data
     */
    getAnalytics: async (landlordId) => {
        return post('pegasus/visionary/tickets/api/tickets/analytics', {
            landlord_id: landlordId,
        });
    },

    /**
     * Get staff members for scheduling
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of staff
     */
    getStaff: async (landlordId) => {
        return post('pegasus/visionary/tickets/api/tickets/staff', {
            landlord_id: landlordId,
        });
    },
};

/**
 * Problem Types Management API
 */
export const problemTypesApi = {
    /**
     * Get all problem types
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of problem types
     */
    getAll: async (landlordId) => {
        return post('pegasus/visionary/tickets/api/tickets/problem-types', {
            landlord_id: landlordId,
        });
    },

    /**
     * Get all problems
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of all problems
     */
    getAllProblems: async (landlordId) => {
        return post('pegasus/visionary/tickets/api/tickets/problems/all', {
            landlord_id: landlordId,
        });
    },

    /**
     * Add a new problem type
     * @param {Object} problemTypeData - Problem type details
     * @returns {Promise<Object>} Add response
     */
    add: async (problemTypeData) => {
        return post('pegasus/visionary/tickets/api/tickets/problem-types/add', problemTypeData);
    },

    /**
     * Update a problem type
     * @param {Object} problemTypeData - Updated problem type details
     * @returns {Promise<Object>} Update response
     */
    update: async (problemTypeData) => {
        return post('pegasus/visionary/tickets/api/tickets/problem-types/update', problemTypeData);
    },

    /**
     * Delete a problem type
     * @param {string} problemTypeId - Problem type ID to delete
     * @returns {Promise<Object>} Delete response
     */
    delete: async (problemTypeId) => {
        return post('pegasus/visionary/tickets/api/tickets/problem-types/delete', {
            problem_type_id: problemTypeId,
        });
    },
};

/**
 * Service Providers Management API
 */
export const serviceProvidersApi = {
    /**
     * Get all service providers
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of service providers
     */
    getAll: async (landlordId) => {
        return post('pegasus/visionary/tickets/api/service-providers', {
            landlord_id: landlordId,
        });
    },

    /**
     * Add a new service provider
     * @param {Object} providerData - Service provider details
     * @returns {Promise<Object>} Add response
     */
    add: async (providerData) => {
        return post('pegasus/visionary/tickets/api/service-providers/add', providerData);
    },

    /**
     * Update a service provider
     * @param {Object} providerData - Updated service provider details
     * @returns {Promise<Object>} Update response
     */
    update: async (providerData) => {
        return post('pegasus/visionary/tickets/api/service-providers/update', providerData);
    },

    /**
     * Delete a service provider
     * @param {string} providerId - Service provider ID to delete
     * @returns {Promise<Object>} Delete response
     */
    delete: async (providerId) => {
        return post('pegasus/visionary/tickets/api/service-providers/delete', {
            service_provider_id: providerId,
        });
    },
};

export default ticketsApi;
